import { useState, useEffect, useRef } from "react";
import debounce from "lodash.debounce";

const INITIAL_FILTERS = {
    priceLevel: "default",
    landmarks: [],
    price: "default",
    amenities: [],
    accommodationType: "default",
    zones: [],
    tripAdvisorStyle: "",
    category: "default", // 👈 new
    city: [],
    distance: "default",
    boardTypes: [],
};

const INITIAL_UNIQUE_VALUES = {
    landmarks: [],
    amenities: [],
    accomomdationTypes: [],
    zones: [],
    cities: [],
    tripAdvisorStyles: [],
    city: [],
    minPrice: 0,
    maxPrice: 0,
    categories: [],
    boardTypes: [],
};

export const useHotelFilter = () => {
    const [originalData, setOriginalData] = useState([]);

    const [hotelsData, setHotelsData] = useState([]);

    const [uniqueValues, setUniqueValues] = useState(INITIAL_UNIQUE_VALUES);

    const [filters, setFilters] = useState(INITIAL_FILTERS);

    const priceLevelsRef = useRef({
        lowMediumPriceLevel: 0,
        mediumHighPriceLevel: 0,
    });

    useEffect(() => {
        if (originalData?.length <= 0) return;
        getUniqueValues();
    }, [originalData]);

    useEffect(() => {
        if (originalData.length > 0) {
            const debouncedFilter = debounce(() => {
                handleFilter();
            }, 300); // adjust delay as needed
            debouncedFilter();
            return () => debouncedFilter.cancel();
        }
    }, [filters]);

    const filterOfferByCategory = (offer, category) => {
        if (!category || category === "default") return true;
        return offer?.category === category;
    };

    const handleFilter = () => {
        if (!originalData || originalData.length === 0) return;

        let offers = originalData.filter((offer) => {
            return (
                filterOfferByZone(offer, filters.city) &&
                filterOfferByPriceLevel(
                    offer,
                    filters.priceLevel,
                    priceLevelsRef.current,
                ) &&
                filterOfferByLandMark(offer, filters.landmarks) &&
                filterOfferByAmenity(offer, filters.amenities) &&
                filterOfferByCategory(offer, filters.category) &&
                filterOfferByAccommodationType(
                    offer,
                    filters.accommodationType,
                ) &&
                filterOfferByTripAdvisorStyles(
                    offer,
                    filters.tripAdvisorStyle,
                ) &&
                filterOfferByBoardType(offer, filters.boards)
            );
        });

        setHotelsData(sortByPrice(sortByDistance(offers)));
    };

    const setData = (data) => {
        if (data?.length <= 0) return;

        setOriginalData(data);
        setHotelsData(data);
    };

    const getMostCommonAmenities = (allHotels, topN = 10) => {
        const amenityCount = {};

        allHotels.forEach((offer) => {
            const amenities = Array.isArray(offer?.amenities)
                ? offer.amenities
                : [];

            amenities.forEach((amenity) => {
                if (!amenity) return;
                amenityCount[amenity] = (amenityCount[amenity] || 0) + 1;
            });
        });

        const excludedAmenities = ["Spanish", "English"];
        const mustIncludeAmenities = [
            "Pool",
            "Beachfront",
            "Kids Activities",
            "Wheelchair access",
            "Fitness center",
            "Breakfast Buffet",
        ];

        // Get top N amenities, excluding the ones we don't want
        const filteredTop = Object.entries(amenityCount)
            .filter(([amenity]) => !excludedAmenities.includes(amenity))
            .sort((a, b) => b[1] - a[1])
            .slice(0, topN)
            .map(([amenity]) => amenity);

        // Merge with mustIncludeAmenities and deduplicate
        const combinedAmenities = Array.from(
            new Set([...mustIncludeAmenities, ...filteredTop]),
        );

        return combinedAmenities;
    };

    const getUniqueValues = () => {
        const allHotels = [...(originalData || [])];

        const boardTypes = allHotels
            .flatMap((offer) => offer?.boardTypes || [])
            .filter((board) => board && board.trim() !== "");

        const uniqueBoardTypes = Array.from(new Set(boardTypes));

        const prices = allHotels.map(
            (offer) => +Math.floor(offer?.pricesFrom || 0),
        );
        const priceLevels = Array.from(new Set(prices)).sort((a, b) => a - b);
        const third = Math.floor(priceLevels.length / 3);

        const lowMediumPriceLevel = priceLevels[third];
        const mediumHighPriceLevel = priceLevels[2 * third];

        priceLevelsRef.current = { lowMediumPriceLevel, mediumHighPriceLevel };

        // Dynamically assign category based on the computed price levels
        const categories = allHotels.map((offer) => {
            const price = offer?.pricesFrom || 0;
            if (price < lowMediumPriceLevel) return "Budget";
            if (price >= lowMediumPriceLevel && price <= mediumHighPriceLevel)
                return "Mid-range";
            return "Luxury";
        });

        // Add the category directly to each hotel object (if you want to use it in filters)
        allHotels.forEach((offer, index) => {
            offer.category = categories[index];
        });

        const mostCommonAmenities = getMostCommonAmenities(allHotels);

        const amenities = allHotels
            .filter(
                (offer) =>
                    Array.isArray(offer?.amenities) &&
                    offer.amenities.length > 0,
            )
            .flatMap((offer) => offer.amenities)
            .filter((amenity) => !mostCommonAmenities.includes(amenity));

        const uniqueAmenities = Array.from(new Set(amenities));

        const allLandmarks =
            allHotels.flatMap((offer) =>
                offer?.interestPoints?.map((point) => point?.poiName),
            ) || [];

        const mostPopularLandmarks = getMostCommonItems(allLandmarks, 10);
        const remainingLandmarks = Array.from(
            new Set(
                allLandmarks.filter((lm) => !mostPopularLandmarks.includes(lm)),
            ),
        );

        const accommodationTypes = allHotels
            .filter((offer) => offer?.accommodationType?.length > 0)
            .map((offer) => offer?.accommodationType);

        // Inside getUniqueValues()
        const cities = allHotels.map((offer) => offer?.city);
        const mostPopularAreas = getMostCommonItems(cities, 10);
        const remainingAreas = Array.from(
            new Set(cities.filter((city) => !mostPopularAreas.includes(city))),
        );

        const zones = allHotels.map((offer) => offer?.city);

        const tripAdvisorStyles = allHotels.flatMap(
            (offer) => offer?.styles || [],
        );

        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        setUniqueValues({
            amenities: Array.from(new Set(amenities)),
            accommodationTypes: Array.from(new Set(accommodationTypes)),
            zones: Array.from(new Set(zones)),
            tripAdvisorStyles: Array.from(new Set(tripAdvisorStyles)),
            categories: ["Budget", "Mid-range", "Luxury"],
            mostCommonAmenities,
            mostPopularAreas, // <- new
            cities: remainingAreas,
            landmarks: remainingLandmarks,
            mostPopularLandmarks,
            minPrice,
            maxPrice,
            boards: uniqueBoardTypes,
        });
    };

    const handleChangeFilter = (name, value) => {
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const clearValues = (resetData = false) => {
        setFilters(INITIAL_FILTERS);

        if (resetData) {
            setHotelsData([]);
            setOriginalData([]);
            setUniqueValues(INITIAL_UNIQUE_VALUES);
        } else {
            // Wait for filters to reset before filtering
            setTimeout(() => {
                handleFilter(); // ✅ Apply clean filter state
            }, 0);
        }
    };

    const sortByPrice = (offers) => {
        if (filters?.price === "default") return offers;

        return offers?.sort((a, b) => {
            const amountA = parseFloat(a?.pricesFrom);
            const amountB = parseFloat(b?.pricesFrom);

            return filters?.price === "high"
                ? amountB - amountA
                : amountA - amountB;
        });
    };

    const sortByDistance = (offers) => {
        if (filters?.distance === "default") return offers;

        return offers?.sort((a, b) => {
            const amountA = parseFloat(a?.DistanceFromCity);
            const amountB = parseFloat(b?.DistanceFromCity);

            return filters?.distance === "closest"
                ? amountA - amountB
                : amountB - amountA;
        });
    };

    return {
        setData,
        hotelsData,
        handleChangeFilter,
        clearValues,
        handleFilter,
        originalData,
        filters,
        uniqueValues,
        priceLevelsRef,
    };
};

const filterOfferByZone = (offer, cities) => {
    if (!Array.isArray(cities) || cities.length === 0) return true;
    return cities.includes(offer?.city);
};

const getMostCommonItems = (items, topN = 10) => {
    const count = {};
    items.forEach((item) => {
        if (!item) return;
        count[item] = (count[item] || 0) + 1;
    });

    return Object.entries(count)
        .sort((a, b) => b[1] - a[1])
        .slice(0, topN)
        .map(([item]) => item);
};

const filterOfferByHotelName = (offer, name) => {
    if (!name?.trim()) return true;

    const searchTerm = name.trim().toLowerCase().replace(/\s+/g, "");
    return offer?.name
        ?.trim()
        ?.toLowerCase()
        .replace(/\s+/g, "")
        .includes(searchTerm);
};

const filterOfferByPriceLevel = (offer, priceLevel, priceLevels) => {
    if (!priceLevel || priceLevel === "default") return true;
    if (priceLevel === "Low") {
        return offer?.pricesFrom < priceLevels.lowMediumPriceLevel;
    } else if (priceLevel === "High") {
        return offer?.pricesFrom > priceLevels.mediumHighPriceLevel;
    } else {
        return (
            offer?.pricesFrom >= priceLevels.lowMediumPriceLevel &&
            offer?.pricesFrom <= priceLevels.mediumHighPriceLevel
        );
    }
};

const filterOfferByLandMark = (offer, landmarks) => {
    if (!Array.isArray(landmarks) || landmarks.length === 0) return true;

    return landmarks.every((landmark) =>
        offer?.interestPoints?.some(
            (point) =>
                point?.poiName?.trim().toLowerCase() ===
                landmark?.trim().toLowerCase(),
        ),
    );
};

const filterOfferByBoardType = (offer, selectedBoard) => {
    if (
        !selectedBoard ||
        selectedBoard.length === 0 ||
        selectedBoard === "default"
    )
        return true;

    return offer?.boardTypes?.some((board) => selectedBoard?.includes(board));
};

const filterOfferByAmenity = (offer, selectedAmenities) => {
    if (!Array.isArray(selectedAmenities) || selectedAmenities.length === 0)
        return true;

    const offerAmenities = Array.isArray(offer?.amenities)
        ? offer.amenities
        : [];

    // Check if **every** selected amenity is included in the offer's amenities
    return selectedAmenities.every((selected) =>
        offerAmenities.includes(selected),
    );
};

const filterOfferByAccommodationType = (offer, acommodationType) => {
    if (!acommodationType || acommodationType === "default") return true;
    return offer?.accommodationType === acommodationType;
};

const filterOfferByTripAdvisorStyles = (offer, tripAdvisorStyle) => {
    if (!tripAdvisorStyle) return true;
    return offer?.styles?.includes(tripAdvisorStyle);
};
